import { Capacitor } from '@capacitor/core';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { auth } from './firebase';

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Best-effort native->web auth synchronization.
 * On iOS, native auth can be available slightly before Firebase Web auth.
 */
export async function ensureFirebaseAuthReadyOnNative(timeoutMs = 3000): Promise<void> {
  if (!Capacitor.isNativePlatform() || auth.currentUser) return;

  // Hard deadline for the whole best-effort sync. On iOS the web SDK runs inside
  // a custom-scheme WKWebView where auth persistence (and therefore
  // authStateReady()) can stall indefinitely. This function is only a courtesy
  // sync — native paths read their token via FirebaseAuthentication directly — so
  // it must never block the caller past this budget.
  const deadline = Date.now() + timeoutMs;

  try {
    const native = await FirebaseAuthentication.getCurrentUser();
    if (!native?.user?.uid) return;

    if (typeof (auth as any).authStateReady === 'function') {
      // authStateReady() can hang forever in the iOS WKWebView, so race it
      // against the remaining budget instead of awaiting it unbounded.
      await Promise.race([
        Promise.resolve((auth as any).authStateReady()).catch(() => undefined),
        wait(Math.max(0, deadline - Date.now())),
      ]);
    }

    while (!auth.currentUser && Date.now() < deadline) {
      // Trigger native token resolution to nudge plugin/web sync lifecycle.
      await FirebaseAuthentication.getIdToken().catch(() => null);
      await wait(150);
    }
  } catch (error) {
    console.warn('⚠️ Native auth readiness check failed:', error);
  }
}

/**
 * Reads an ID token from native plugin first, then falls back to web auth.
 */
export async function getBestEffortIdToken(): Promise<string | null> {
  if (Capacitor.isNativePlatform()) {
    try {
      const nativeUser = await FirebaseAuthentication.getCurrentUser();
      if (nativeUser?.user?.uid) {
        const tokenResult = await FirebaseAuthentication.getIdToken();
        if (tokenResult?.token) return tokenResult.token;
      }
    } catch {
      // Fall through to web token fallback.
    }
  }

  if (auth.currentUser) {
    try {
      return await auth.currentUser.getIdToken();
    } catch {
      return null;
    }
  }

  return null;
}
